/**
 * ConversationsDashboard — « Tableau de bord » of the Conversations section.
 *
 * Scope: THIS SECTION ONLY. The aggregated cross-feature view (prompts +
 * conversations + …) belongs to the Reports section, not here.
 * Audience: an agency's client, or a marketing director — so every block states its
 * denominator and a block with no data is ABSENT, never a zero-filled placeholder.
 *
 * Design: docs/plans/conversations/09-dashboard.md (§9.6 lot 9a), reference mockup
 * `09-dashboard-mockup.html`. Charts are the hand-rolled SVG primitives in `../viz`
 * (Chart.js cannot do null-broken lines + event markers + leader lines without
 * plugins); it keeps all the copy, so `../viz` never imports i18n.
 *
 * Self-contained: own filter bar (persona tag, engine, zone, timeframe) + one
 * debounced `GET /brands/:brandId/conversations/dashboard?…`.
 *
 * Seven cards, all against SHIPPED fields:
 *   hero Présence · Part de voix · Citée comme source   (StatTile ×3)
 *   « Présence dans le temps »        — full width, broken at nulls
 *   « Résultat par intention »        — full width, TWO panes, two denominators
 *   « Présence par moteur » · « Présence par zone »
 *   « Présence par type de cible » · « Les cibles les plus citées »
 *
 * The three blocks of the mockup that need a row that does not exist yet (coverage
 * over time with its « +N cibles » event markers, the tag×type heat map, per-tag
 * segments) are NOT stubbed here — see §9.3 gaps 1/3/4/5.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Link,
  Popover,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TimeframeSelector, { type TimeframeValue, resolveTimeframe } from '../common/TimeframeSelector';
import { engineDisplay, engineIconSrc, formatDayShort } from './personaFormat';
import {
  ChartCard,
  EmptyNote,
  RankedBars,
  StackedBars,
  StatTile,
  TrendLine,
  VizTable,
  VIZ,
  bucketK,
  bucketVals,
  bucketize,
  fmtInt,
  fmtPct,
  setVizLocale,
  type RankedBarsRow,
  type TrendLineSeries,
} from '../viz';
import { api } from '../../services/api';
import {
  type ConversationBrandRollup,
  type ConversationPersonaSummary,
  type ConversationEngine,
  CONVERSATION_ENGINES,
  TARGET_CATEGORIES,
} from '@traaker/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardResponse {
  window: string;
  series: { d: string; presence: number | null; n: number }[];
  /**
   * The SAME day axis as `series`, un-averaged, one entry per persona intent that
   * has data. `{}` on the tag path (PSUM# rows carry a day-less `spark`).
   */
  seriesByIntent?: Record<string, { d: string; presence: number | null }[]>;
  presence: number | null;
  shareOfVoice: number | null;
  sourceRate: number | null;
  /** The same three rates over the preceding window. `null` ⇒ no comparison exists. */
  prev: { presence: number | null; shareOfVoice: number | null; sourceRate: number | null } | null;
  n: number;
  outcome: { win: number; part: number; lose: number };
  byEngine: Record<string, { presence: number | null; n: number }>;
  byZone: Record<string, { presence: number | null; n: number }>;
  byCategory: (number | null)[];
  byIntent: Record<
    string,
    { presence: number | null; n: number; win: number; part: number; lose: number }
  >;
  /** Per-TURN observed intent (§9.2 block ②, left pane). `{}` on the tag path. */
  byObservedIntent: Record<string, { present: number; turns: number }>;
  tagFilter: string | null;
  tagPersonaCount: number;
  topTargetsByCategory?: Record<string, { value: string; targetId: string; cit: number }[]>;
  /**
   * Block ⑧ — one row per measured UTC day. `[]` under a tag filter: a day row is
   * brand-grain and carries no persona dimension, so there is nothing to filter.
   * A day with launches but no citation IS present, with zeros; a day with no launch
   * has no row at all, and the chart must break there rather than draw a 0.
   */
  targetDays?: {
    d: string;
    citByCategory: number[];
    distinctByCategory: number[];
    truncated: boolean;
  }[];
  /** Block ⑧ — referential changes, already folded to one entry per day. */
  targetEvents?: { d: string; cause: string; importId?: string; deltaByCategory: number[] }[];
}

interface TagOption {
  tag: string;
  personaCount: number;
}

export interface ConversationsDashboardProps {
  brandId: string;
  brandRows: ConversationBrandRollup[];
  personasSummary: ConversationPersonaSummary[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Persona intents — the configured grain. Frozen list (D-intents), so a fixed order. */
const PERSONA_INTENTS = ['learn', 'discover', 'compare'] as const;

/**
 * Observed (per-turn) intents — all 7 categories + the 'other' residual.
 * Order matches the buyer-journey taxonomy: learn → discover → compare →
 * intra_compare → authority → transact → troubleshoot → other.
 * Only intents with > 0 turns in the window are rendered.
 */
const OBSERVED_INTENTS = ['learn', 'discover', 'compare', 'intra_compare', 'authority', 'transact', 'troubleshoot', 'other'] as const;

/** Ordered good → neutral → bad. Status hues, always shipped with their label + legend. */
const OUTCOME_COLORS = { win: VIZ.good, part: VIZ.warning, lose: VIZ.critical };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * A zone key is `<ISO2>-<slug>` (`FR-toulouse`). The flag comes ONLY from that
 * structured prefix — never from the first two letters of a place name, which is how
 * « Paris » becomes 🇵🇦 (CLAUDE.md, Cibles/zone invariant).
 */
function zoneLabel(zone: string): string {
  const m = /^([A-Za-z]{2})-(.+)$/.exec(zone);
  if (!m) return zone;
  const flag = String.fromCodePoint(
    ...m[1].toUpperCase().split('').map((c) => 0x1f1e0 + c.charCodeAt(0) - 65),
  );
  return `${flag} ${m[2]}`;
}

/**
 * Deep link into the Cibles referential: the category tab, plus the probe prefilled in
 * its search box (`Targets.tsx` reads both params). `#cibles` is what expands and scrolls
 * to the section inside the GEO Strategy page, which is where Cibles is mounted.
 */
function cibleHref(category: string, probe: string): string {
  const p = new URLSearchParams({ cibles: category, cibleQ: probe });
  return `/geo-strategy?${p.toString()}#cibles`;
}

/** Percentage-POINT delta for a StatTile, or `undefined` when there is no comparison. */
function pointDelta(now: number | null, before: number | null | undefined) {
  if (now == null || before == null) return undefined;
  return (now - before) * 100;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={150} />
        ))}
      </Box>
      <Skeleton variant="rounded" height={360} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        <Skeleton variant="rounded" height={260} />
        <Skeleton variant="rounded" height={260} />
      </Box>
    </Box>
  );
}

function EngineFilterButton({
  selectedEngines,
  onChange,
}: {
  selectedEngines: Set<ConversationEngine>;
  onChange: (next: Set<ConversationEngine>) => void;
}) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const toggle = (engine: ConversationEngine) => {
    const next = new Set(selectedEngines);
    if (next.has(engine)) next.delete(engine);
    else next.add(engine);
    onChange(next);
  };

  const allSelected = selectedEngines.size === 0;
  const label = allSelected
    ? t('conversations.dash.allEngines')
    : selectedEngines.size === 1
    ? engineDisplay([...selectedEngines][0])
    : t('conversations.dash.engineCount', { count: selectedEngines.size });

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          border: '1px solid rgba(0,0,0,0.2)',
          cursor: 'pointer',
          fontSize: '0.8rem',
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
          userSelect: 'none',
        }}
      >
        <FilterListIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <span>{label}</span>
        <KeyboardArrowDownIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, minWidth: 180 } }}
      >
        <FormControlLabel
          control={
            <Checkbox size="small" checked={allSelected} onChange={() => onChange(new Set())} />
          }
          label={<Typography variant="body2">{t('conversations.dash.allEnginesShort')}</Typography>}
          sx={{ display: 'flex', mb: 0.5 }}
        />
        {CONVERSATION_ENGINES.map((engine) => (
          <FormControlLabel
            key={engine}
            control={
              <Checkbox
                size="small"
                checked={selectedEngines.has(engine)}
                onChange={() => toggle(engine)}
              />
            }
            label={<Typography variant="body2">{engineDisplay(engine)}</Typography>}
            sx={{ display: 'flex' }}
          />
        ))}
      </Popover>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ConversationsDashboard({
  brandId,
  brandRows,
  personasSummary,
}: ConversationsDashboardProps) {
  const { t, i18n } = useTranslation();
  setVizLocale(i18n.language);

  // Filter state
  const [selectedTag, setSelectedTag] = useState<TagOption | null>(null);
  const [selectedEngines, setSelectedEngines] = useState<Set<ConversationEngine>>(new Set());
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [timeframe, setTimeframe] = useState<TimeframeValue>({ kind: 'relative', days: 30 });
  const [topTab, setTopTab] = useState(0);
  /** Block ⑧: 0 = Citations (a cible cited 10× counts 10), 1 = Cibles distinctes (counts 1). */
  const [targetTab, setTargetTab] = useState(0);

  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Zones come from the brand rollup rows: PSUM# rows carry no zone breakdown.
  const zoneOptions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const row of brandRows) {
      for (const zone of Object.keys(row.windows?.['30d']?.byZone ?? {})) set.add(zone);
    }
    return Array.from(set).sort();
  }, [brandRows]);

  useEffect(() => {
    if (!brandId) return;
    // `getTagInventory` returns `{ tags: [...] }`, NOT a bare array — typing it as
    // `TagOption[]` made `[null, ...tagOptions]` spread a plain object and throw
    // "not iterable" into the error boundary (fixed 2026-10-03).
    api
      .get<{ tags?: TagOption[] }>(`/brands/${brandId}/conversations/tags`)
      .then((resp) => setTagOptions(resp?.tags ?? []))
      .catch(() => {});
  }, [brandId]);

  // Single effect: fetch whenever any filter changes, 300 ms debounce. One effect +
  // direct state deps avoids the stale-closure / double-fire problem a useCallback
  // intermediary introduces.
  useEffect(() => {
    if (!brandId) return;
    const days =
      timeframe.kind === 'relative'
        ? timeframe.days
        : Math.round(
            (new Date(resolveTimeframe(timeframe).to).getTime() -
              new Date(resolveTimeframe(timeframe).from).getTime()) /
              86400000,
          );
    const win: string = days <= 7 ? '7d' : days <= 30 ? '30d' : '90d';
    const params = new URLSearchParams({ window: win });
    if (selectedTag) params.set('tag', selectedTag.tag);
    if (selectedEngines.size > 0) params.set('engine', [...selectedEngines].join(','));
    if (selectedZone) params.set('zone', selectedZone);

    const timer = setTimeout(() => {
      setLoading(true);
      api
        .get<DashboardResponse>(`/brands/${brandId}/conversations/dashboard?${params.toString()}`)
        .then((resp) => setData(resp ?? null))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, selectedTag, selectedEngines, selectedZone, timeframe]);

  // ---- derived view models -------------------------------------------------

  const intentLabel = (id: string) =>
    id === 'other'
      ? t('conversations.dash.intentOther')
      : t(`conversations.intent${id.charAt(0).toUpperCase()}${id.slice(1)}`);

  // Slice the full 90-day series (newest-last) to the requested window before bucketing.
  const windowDays = data?.window === '7d' ? 7 : data?.window === '30d' ? 30 : 90;
  const visibleSeries = useMemo(
    () => (data?.series ?? []).slice(-windowDays),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.series, data?.window],
  );

  const trend = useMemo(() => bucketize(visibleSeries), [visibleSeries]);

  /**
   * One line per persona intent + the all-intents average on top. The average is NOT
   * a category, so it wears ink and a heavier stroke instead of taking a categorical
   * hue off a real series; the three intents keep their fixed hue order (never cycled,
   * so an intent with no data this window does not repaint the others).
   */
  const trendSeries = useMemo(() => {
    const k = bucketK(visibleSeries.length);
    const per = PERSONA_INTENTS.map((id, i) => {
      // per-intent series shares the same 90-day axis — slice to the same window
      const pts = data?.seriesByIntent?.[id]?.slice(-windowDays);
      if (!pts || !pts.some((p) => p.presence != null)) return null;
      return {
        key: id,
        label: intentLabel(id),
        color: VIZ.series[i],
        points: bucketVals(pts.map((p) => p.presence), k),
      };
    }).filter((s): s is NonNullable<typeof s> => s !== null);
    // Alone on the chart it is not "the average of" anything the reader can see, so
    // it keeps the plain « Présence » name — and no legend is drawn for one series.
    const avg = {
      key: 'avg',
      label: per.length
        ? t('conversations.dash.blockTrendAvg')
        : t('conversations.dash.blockTrendSeries'),
      color: VIZ.ink,
      width: per.length ? 2.5 : 2,
      points: trend.map((p) => p.presence),
    };
    return [avg, ...per];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trend, data?.seriesByIntent, data?.series?.length, i18n.language]);

  const observedRows = useMemo<RankedBarsRow[]>(() => {
    const src = data?.byObservedIntent ?? {};
    return OBSERVED_INTENTS.filter((k) => (src[k]?.turns ?? 0) > 0).map((k) => ({
      label: t(`conversations.persona.observedIntent_${k}`),
      value: src[k].present / src[k].turns,
      n: src[k].turns,
      tip: [t('conversations.dash.paneObservedFoot')],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.byObservedIntent, i18n.language]);

  const outcomeRows = useMemo(
    () =>
      PERSONA_INTENTS.filter((id) => {
        const v = data?.byIntent?.[id];
        return v && v.win + v.part + v.lose > 0;
      }).map((id) => {
        const v = data!.byIntent[id];
        return {
          label: intentLabel(id),
          n: v.n,
          parts: [
            {
              key: 'win',
              label: t('conversations.dash.outcomeWin'),
              value: v.win,
              color: OUTCOME_COLORS.win,
            },
            {
              key: 'part',
              label: t('conversations.dash.outcomePart'),
              value: v.part,
              color: OUTCOME_COLORS.part,
            },
            {
              key: 'lose',
              label: t('conversations.dash.outcomeLose'),
              value: v.lose,
              color: OUTCOME_COLORS.lose,
            },
          ],
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.byIntent, i18n.language],
  );

  const engineRows = useMemo<RankedBarsRow[]>(
    () =>
      Object.entries(data?.byEngine ?? {})
        .filter(([, v]) => v.presence != null)
        .sort((a, b) => (b[1].presence ?? 0) - (a[1].presence ?? 0))
        .map(([engine, v]) => ({
          label: engineDisplay(engine),
          value: v.presence,
          n: v.n,
          iconSrc: engineIconSrc(engine),
        })),
    [data?.byEngine],
  );

  const zoneAll = useMemo(
    () =>
      Object.entries(data?.byZone ?? {})
        .filter(([, v]) => v.presence != null)
        .sort((a, b) => (b[1].presence ?? 0) - (a[1].presence ?? 0)),
    [data?.byZone],
  );
  const zoneRows = useMemo<RankedBarsRow[]>(
    () => zoneAll.slice(0, 15).map(([zone, v]) => ({ label: zoneLabel(zone), value: v.presence, n: v.n })),
    [zoneAll],
  );

  // D53 — a category with no cible in the referential is OMITTED, never shown as 0 %.
  const categoryRows = useMemo<RankedBarsRow[]>(
    () =>
      (data?.byCategory ?? [])
        .map((v, i) => ({ v, slug: TARGET_CATEGORIES[i] }))
        .filter((r) => r.v != null && r.slug)
        .sort((a, b) => (b.v ?? 0) - (a.v ?? 0))
        .map((r) => ({ label: t(`cibles.tab.${r.slug}`), value: r.v })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.byCategory, i18n.language],
  );

  // One tab per category that actually has cited targets.
  const topCats = useMemo(
    () =>
      TARGET_CATEGORIES.filter((c) => (data?.topTargetsByCategory?.[c]?.length ?? 0) > 0),
    [data?.topTargetsByCategory],
  );
  const activeTopCat = topCats[Math.min(topTab, topCats.length - 1)];
  /**
   * `value` is the folded PROBE, not the target's label — the label is only resolvable
   * from the Cibles manifest by `targetId`, and is deliberately not stored (a rename
   * would freeze a stale one). So the row shows the probe, readably un-piped, and the
   * link hands the referential enough to find the fiche: there is no per-target route
   * (no `GET /targets/:targetId`, no detail page), only the Cibles section itself.
   */
  const topRows = useMemo<RankedBarsRow[]>(
    () =>
      (data?.topTargetsByCategory?.[activeTopCat ?? ''] ?? []).slice(0, 12).map((e) => {
        const parts = e.value.split('|').map((s) => s.trim()).filter(Boolean);
        const label = parts.join(' · ') || e.value;
        return {
          label,
          value: e.cit,
          href: activeTopCat ? cibleHref(activeTopCat, parts[0] ?? e.value) : undefined,
          tip: activeTopCat ? [t('conversations.dash.blockTopOpenRow', { label })] : undefined,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.topTargetsByCategory, activeTopCat, i18n.language],
  );

  /**
   * ⑧ « Cibles citées dans le temps » — the two tabs the user asked for, on the day
   * axis `trend` already established (same slice, same bucket width, so the two charts
   * cannot disagree about what "last week" means).
   *
   * Tab 0 counts every citation (a product cited 10 times → 10); tab 1 counts each
   * cible once (→ 1). The axis is a RAW COUNT on both, as decided — a per-100-turns
   * variant was offered and declined — which is why the event markers below are
   * load-bearing rather than decoration: a jump with a marker beside it is an import,
   * a jump without one is real.
   *
   * BOTH tabs bucket as a per-day MEAN, never a sum. Tab 1 could legally sum, but tab 1
   * cannot: distinct counts do not add across days (a cible cited Monday and Tuesday is
   * one cible, not two), and the API cannot union at read time. Meaning them both keeps
   * the two tabs comparable and keeps a partial week at the window edge from reading as
   * a collapse.
   */
  const cibleTrend = useMemo(() => {
    const byDay = new Map((data?.targetDays ?? []).map((r) => [r.d, r]));
    const days = visibleSeries.map((p) => p.d);
    const k = bucketK(days.length);

    const valueAt = (r: NonNullable<DashboardResponse['targetDays']>[number], i: number) =>
      Number((targetTab === 0 ? r.citByCategory : r.distinctByCategory)[i] ?? 0) || 0;

    // A measured day with no citation is 0; an unmeasured day is null and BREAKS the
    // line. Days before this counter shipped have no row and are therefore unmeasured,
    // which is the honest reading — not a brand that was suddenly never cited.
    const daily = TARGET_CATEGORIES.map((_, i) =>
      days.map((d) => {
        const r = byDay.get(d);
        return r ? valueAt(r, i) : null;
      }),
    );

    /**
     * Selection is ranked by the CITATIONS total — never by the active tab — so
     * switching tabs cannot repaint a category, and hues are then handed out in
     * canonical TARGET_CATEGORIES order rather than by rank. Colour follows the
     * entity, not its position in a leaderboard.
     *
     * A category that is zero across the whole window is dropped, not drawn flat at 0
     * (D53's « omitted, never 0 » applied to a line).
     */
    const citTotals = TARGET_CATEGORIES.map((_, i) =>
      days.reduce((s, d) => s + Number(byDay.get(d)?.citByCategory?.[i] ?? 0), 0),
    );
    const ranked = TARGET_CATEGORIES.map((c, i) => ({ c, i, total: citTotals[i] }))
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);

    const kept = ranked.slice(0, VIZ.series.length).sort((a, b) => a.i - b.i);
    const folded = ranked.slice(VIZ.series.length);

    const series: TrendLineSeries[] = kept.map((x, slot) => ({
      key: x.c,
      label: t(`cibles.tab.${x.c}`),
      color: VIZ.series[slot],
      points: bucketVals(daily[x.i], k),
    }));

    /**
     * More categories than hues, so the tail folds into one « Autres » line — the
     * palette is fixed-order and never cycled past slot 4. « Autres » is an AGGREGATE,
     * not a category, so like the average on the presence chart it wears ink instead of
     * taking a hue off a real series.
     */
    if (folded.length > 0) {
      const summed = days.map((_, di) => {
        const vals = folded.map((x) => daily[x.i][di]).filter((v): v is number => v != null);
        return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
      });
      series.push({
        key: '__other',
        label: t('conversations.dash.blockCiblesOther'),
        color: VIZ.ink2,
        points: bucketVals(summed, k),
      });
    }

    // Events land on the bucket that contains their day — `bucketize` chunks from
    // index 0, so the bucket index is a plain floor division on the same axis.
    const dayIndex = new Map(days.map((d, i) => [d, i]));
    const events: { index: number; label: string }[] = [];
    for (const e of data?.targetEvents ?? []) {
      const di = dayIndex.get(e.d);
      if (di == null) continue;
      // Label on the category that moved most; the table view carries the rest.
      let best = 0;
      for (let i = 1; i < e.deltaByCategory.length; i++) {
        if (Math.abs(e.deltaByCategory[i] ?? 0) > Math.abs(e.deltaByCategory[best] ?? 0)) best = i;
      }
      const delta = e.deltaByCategory[best] ?? 0;
      if (delta === 0) continue;
      // « +38 Produits », not « 38 produits ajoutés »: a sign needs no plural and no
      // gender agreement, and it fits the chart's 24px label band in both languages.
      const sign = delta > 0 ? '+' : '−';
      events.push({
        index: Math.floor(di / k),
        label: `${sign}${fmtInt(Math.abs(delta))} ${t(`cibles.tab.${TARGET_CATEGORIES[best]}`)}`,
      });
    }

    const eventsByDay = new Map<string, string[]>();
    for (const e of data?.targetEvents ?? []) {
      const parts = e.deltaByCategory
        .map((v, i) =>
          v ? `${v > 0 ? '+' : '−'}${fmtInt(Math.abs(v))} ${t(`cibles.tab.${TARGET_CATEGORIES[i]}`)}` : '',
        )
        .filter(Boolean);
      if (parts.length) eventsByDay.set(e.d, parts);
    }

    return {
      series,
      events,
      eventsByDay,
      k,
      days,
      // Any day in the window hitting a cap makes BOTH numbers floors, so the card has
      // to say so rather than present them as exact.
      truncated: (data?.targetDays ?? []).some((r) => r.truncated),
      firstDay: (data?.targetDays ?? [])[0]?.d ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.targetDays, data?.targetEvents, visibleSeries, targetTab, i18n.language]);

  const personaCount = data?.tagFilter ? data.tagPersonaCount : personasSummary.length;
  const observedTurns = useMemo(
    () => Object.values(data?.byObservedIntent ?? {}).reduce((s, v) => s + (v.turns ?? 0), 0),
    [data?.byObservedIntent],
  );
  const filtersNarrowKpis = selectedEngines.size > 0 || !!selectedZone;

  /**
   * « 1 persona » / « 2 personas ». `count` drives i18next's plural selection and
   * `n` carries the thousands-separated form — a single `{{count}}` would print the
   * raw integer, and three counts in one sentence cannot share one plural key.
   */
  const nOf = (key: string, v: number) =>
    t(`conversations.dash.${key}`, { count: v, n: fmtInt(v) });

  const notMeasured = t('conversations.dash.notMeasured');
  const emptyNote = t('conversations.dash.noData');
  const unmeasured = t('conversations.dash.notMeasuredShort');
  const viewLabels = {
    chartLabel: t('conversations.dash.viewChart'),
    tableLabel: t('conversations.dash.viewTable'),
  };

  return (
    <Box>
      {/* Filter bar — always shown, even before the first measurement lands */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2.5,
          flexWrap: 'wrap',
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        {/* Persona tag — NOT a Cibles tag (§9.4b): this filters which personas count */}
        <Autocomplete<TagOption | null>
          size="small"
          options={[null, ...tagOptions]}
          value={selectedTag}
          onChange={(_, v) => setSelectedTag(v)}
          getOptionLabel={(opt) =>
            opt ? `${opt.tag} (${opt.personaCount})` : t('conversations.dash.allPersonas')
          }
          isOptionEqualToValue={(opt, val) => opt?.tag === val?.tag}
          renderOption={(props, opt) => (
            <li {...props}>
              {opt ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Chip label={opt.tag} size="small" sx={{ maxWidth: 140 }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('conversations.dash.personaCount', { count: opt.personaCount })}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">{t('conversations.dash.allPersonas')}</Typography>
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('conversations.dash.allPersonas')}
              sx={{ minWidth: 180 }}
            />
          )}
          sx={{ minWidth: 180 }}
        />

        <EngineFilterButton selectedEngines={selectedEngines} onChange={setSelectedEngines} />

        <Autocomplete<string>
          size="small"
          options={['', ...zoneOptions]}
          value={selectedZone}
          onChange={(_, v) => setSelectedZone(v ?? '')}
          getOptionLabel={(opt) => (opt ? zoneLabel(opt) : t('conversations.dash.allZones'))}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('conversations.dash.allZones')}
              sx={{ minWidth: 160 }}
            />
          )}
          sx={{ minWidth: 160 }}
        />

        {/* The CloudWatch-style selector, unchanged */}
        <Box sx={{ ml: 'auto' }}>
          <TimeframeSelector value={timeframe} onChange={setTimeframe} maxDays={90} />
        </Box>
      </Box>

      {loading ? (
        <LoadingSkeleton />
      ) : !data ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyNote}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          {/* ---- hero tiles ---------------------------------------------- */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
            }}
          >
            <StatTile
              hero
              label={t('conversations.dash.kpiPresence')}
              value={data.presence != null ? fmtPct(data.presence) : notMeasured}
              delta={
                pointDelta(data.presence, data.prev?.presence) != null
                  ? {
                      value: pointDelta(data.presence, data.prev?.presence)!,
                      suffix: `pts ${t('conversations.dash.vsPrevious')}`,
                    }
                  : undefined
              }
              foot={
                data.n === 0
                  ? t('conversations.dash.kpiPresenceFootEmpty')
                  : observedTurns > 0
                  ? t('conversations.dash.kpiPresenceFoot', {
                      turns: nOf('nTurnsMeasured', observedTurns),
                      runs: nOf('nRuns', data.n),
                      personas: nOf('nPersonas', personaCount),
                    })
                  : t('conversations.dash.kpiPresenceFootNoTurns', {
                      runs: nOf('nRunsMeasured', data.n),
                      personas: nOf('nPersonas', personaCount),
                    })
              }
            />
            <StatTile
              label={t('conversations.dash.kpiSov')}
              value={data.shareOfVoice != null ? fmtPct(data.shareOfVoice) : notMeasured}
              delta={
                pointDelta(data.shareOfVoice, data.prev?.shareOfVoice) != null
                  ? {
                      value: pointDelta(data.shareOfVoice, data.prev?.shareOfVoice)!,
                      suffix: `pts ${t('conversations.dash.vsPrevious')}`,
                    }
                  : undefined
              }
              foot={
                <>
                  {t('conversations.dash.kpiSovFoot')}
                  {/* The endpoint does not re-fold these three rates under an engine or
                      zone filter (§9.4) — say so rather than show a filtered-looking
                      number that is in fact global. */}
                  {filtersNarrowKpis && (
                    <Box component="span" sx={{ display: 'block', mt: 0.5, color: VIZ.muted }}>
                      {t('conversations.dash.kpiUnfiltered')}
                    </Box>
                  )}
                </>
              }
            />
            <StatTile
              label={t('conversations.dash.kpiSource')}
              value={data.sourceRate != null ? fmtPct(data.sourceRate) : notMeasured}
              delta={
                pointDelta(data.sourceRate, data.prev?.sourceRate) != null
                  ? {
                      value: pointDelta(data.sourceRate, data.prev?.sourceRate)!,
                      suffix: `pts ${t('conversations.dash.vsPrevious')}`,
                    }
                  : undefined
              }
              foot={t('conversations.dash.kpiSourceFoot')}
            />
          </Box>

          {/* ---- Présence dans le temps ---------------------------------- */}
          <ChartCard
            sx={{ gridColumn: '1 / -1' }}
            title={t('conversations.dash.blockTrend')}
            hint={t('conversations.dash.blockTrendHint')}
            {...viewLabels}
            table={
              <VizTable
                columns={[
                  t('conversations.dash.colDay'),
                  ...trendSeries.map((s) => s.label),
                  t('conversations.dash.colConversations'),
                ]}
                rows={trend.map((p, i) => [
                  formatDayShort(p.label, i18n.language),
                  ...trendSeries.map((s) =>
                    s.points[i] != null ? fmtPct(s.points[i] as number) : unmeasured,
                  ),
                  fmtInt(p.n),
                ])}
              />
            }
          >
            <TrendLine
              labels={trend.map((p) => formatDayShort(p.label, i18n.language))}
              series={trendSeries}
              yMax={1}
              emptyNote={emptyNote}
              unmeasuredNote={unmeasured}
            />
          </ChartCard>

          {/* ---- ⑧ Cibles citées dans le temps --------------------------
              A SECOND trend block, deliberately not extra lines on the one above:
              seven more lines would make that chart unreadable, and this y-axis is a
              COUNT where that one is a rate — one axis per chart, never two. */}
          <ChartCard
            sx={{ gridColumn: '1 / -1' }}
            title={t('conversations.dash.blockCibles')}
            hint={
              targetTab === 0
                ? t('conversations.dash.blockCiblesHintCit')
                : t('conversations.dash.blockCiblesHintDistinct')
            }
            {...viewLabels}
            right={
              <Tabs
                value={targetTab}
                onChange={(_, v) => setTargetTab(v)}
                variant="scrollable"
                scrollButtons={false}
                sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, fontSize: '0.75rem' } }}
              >
                <Tab label={t('conversations.dash.blockCiblesTabCit')} />
                <Tab label={t('conversations.dash.blockCiblesTabDistinct')} />
              </Tabs>
            }
            table={
              <VizTable
                columns={[
                  t('conversations.dash.colDay'),
                  ...cibleTrend.series.map((s) => s.label),
                  t('conversations.dash.colRefChange'),
                ]}
                align={[
                  'left',
                  ...cibleTrend.series.map(() => 'right' as const),
                  'left' as const,
                ]}
                rows={trend.map((p, i) => [
                  formatDayShort(p.label, i18n.language),
                  ...cibleTrend.series.map((s) =>
                    s.points[i] != null ? fmtInt(Math.round(s.points[i] as number)) : unmeasured,
                  ),
                  // The chart's flag can only name the category that moved most; the
                  // table is where the whole change is legible.
                  (cibleTrend.eventsByDay.get(p.label) ?? []).join(', '),
                ])}
              />
            }
          >
            <TrendLine
              labels={trend.map((p) => formatDayShort(p.label, i18n.language))}
              series={cibleTrend.series}
              events={cibleTrend.events}
              format={(v) => fmtInt(Math.round(v))}
              emptyNote={emptyNote}
              unmeasuredNote={unmeasured}
            />
            {(cibleTrend.truncated || cibleTrend.firstDay) && (
              <Typography sx={{ fontSize: 11.5, color: VIZ.muted, mt: 1 }}>
                {cibleTrend.firstDay &&
                  t('conversations.dash.blockCiblesSince', {
                    date: formatDayShort(cibleTrend.firstDay, i18n.language),
                  })}
                {cibleTrend.truncated && ` ${t('conversations.dash.blockCiblesTruncated')}`}
              </Typography>
            )}
          </ChartCard>

          {/* ---- Résultat par intention (two grains, two denominators) ---- */}
          <ChartCard
            sx={{ gridColumn: '1 / -1' }}
            title={t('conversations.dash.blockIntent')}
            hint={t('conversations.dash.blockIntentHint')}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: VIZ.ink, mb: 1 }}>
                  {t('conversations.dash.paneObserved')}
                </Typography>
                {observedRows.length > 0 ? (
                  <>
                    <RankedBars
                      data={observedRows}
                      maxValue={1}
                      emptyNote={emptyNote}
                      unmeasuredNote={unmeasured}
                    />
                    <Typography sx={{ fontSize: 12, color: VIZ.muted, mt: 1 }}>
                      {t('conversations.dash.paneObservedFoot')}
                    </Typography>
                  </>
                ) : (
                  <EmptyNote>
                    {data.tagFilter
                      ? t('conversations.dash.paneObservedUnavailable')
                      : notMeasured}
                  </EmptyNote>
                )}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: VIZ.ink, mb: 1 }}>
                  {t('conversations.dash.paneOutcome')}
                </Typography>
                {outcomeRows.length > 0 ? (
                  <>
                    <StackedBars rows={outcomeRows} emptyNote={emptyNote} />
                    <Typography sx={{ fontSize: 12, color: VIZ.muted, mt: 1 }}>
                      {t('conversations.dash.paneOutcomeFoot')}
                    </Typography>
                  </>
                ) : (
                  <EmptyNote>{notMeasured}</EmptyNote>
                )}
              </Box>
            </Box>
          </ChartCard>

          {/* ---- Présence par moteur ------------------------------------- */}
          <ChartCard
            title={t('conversations.dash.blockEngine')}
            hint={t('conversations.dash.blockEngineHint')}
            {...viewLabels}
            table={
              <VizTable
                columns={[t('conversations.dash.colEngine'), t('conversations.dash.colPresence'), t('conversations.dash.colConversations')]}
                rows={engineRows.map((r) => [
                  <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {r.iconSrc && (
                      <Box component="img" src={r.iconSrc} alt="" sx={{ width: 15, height: 15 }} />
                    )}
                    <span>{r.label}</span>
                  </Box>,
                  r.value != null ? fmtPct(r.value) : unmeasured,
                  fmtInt(r.n ?? 0),
                ])}
              />
            }
          >
            <RankedBars
              data={engineRows}
              maxValue={1}
              emptyNote={notMeasured}
              unmeasuredNote={unmeasured}
            />
          </ChartCard>

          {/* ---- Présence par zone --------------------------------------- */}
          <ChartCard
            title={t('conversations.dash.blockZone')}
            hint={t('conversations.dash.blockZoneHint')}
            {...viewLabels}
            table={
              <VizTable
                columns={[t('conversations.dash.colZone'), t('conversations.dash.colPresence'), t('conversations.dash.colConversations')]}
                rows={zoneAll.map(([zone, v]) => [
                  zoneLabel(zone),
                  v.presence != null ? fmtPct(v.presence) : unmeasured,
                  fmtInt(v.n),
                ])}
              />
            }
          >
            <RankedBars
              data={zoneRows}
              maxValue={1}
              emptyNote={notMeasured}
              unmeasuredNote={unmeasured}
            />
            {zoneAll.length > zoneRows.length && (
              <Typography sx={{ fontSize: 12, color: VIZ.muted, mt: 1 }}>
                {t('conversations.dash.zonesTruncated', {
                  shown: zoneRows.length,
                  total: zoneAll.length,
                })}
              </Typography>
            )}
          </ChartCard>

          {/* ---- Présence par type de cible ------------------------------ */}
          <ChartCard
            title={t('conversations.dash.blockCategory')}
            hint={t('conversations.dash.blockCategoryHint')}
            {...viewLabels}
            table={
              <VizTable
                columns={[t('conversations.dash.colCategory'), t('conversations.dash.colPresence')]}
                rows={categoryRows.map((r) => [
                  r.label,
                  r.value != null ? fmtPct(r.value) : unmeasured,
                ])}
              />
            }
          >
            <RankedBars
              data={categoryRows}
              maxValue={1}
              emptyNote={notMeasured}
              unmeasuredNote={unmeasured}
            />
          </ChartCard>

          {/* ---- Les cibles les plus citées ------------------------------ */}
          <ChartCard
            title={t('conversations.dash.blockTop')}
            hint={t('conversations.dash.blockTopHint')}
            {...viewLabels}
            right={
              topCats.length > 1 ? (
                <Tabs
                  value={Math.min(topTab, topCats.length - 1)}
                  onChange={(_, v) => setTopTab(v)}
                  variant="scrollable"
                  scrollButtons={false}
                  sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, fontSize: '0.75rem' } }}
                >
                  {topCats.map((c) => (
                    <Tab key={c} label={t(`cibles.tab.${c}`)} />
                  ))}
                </Tabs>
              ) : undefined
            }
            table={
              <VizTable
                columns={[
                  t('conversations.dash.colTarget'),
                  t('conversations.dash.colCitations'),
                  t('conversations.dash.colOpen'),
                ]}
                align={['left', 'right', 'right']}
                rows={topRows.map((r) => [
                  r.label,
                  fmtInt(r.value ?? 0),
                  r.href ? (
                    <Link
                      key={r.label}
                      href={r.href}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ fontSize: 12.5 }}
                    >
                      {t('conversations.dash.blockTopOpenAll')}
                    </Link>
                  ) : (
                    ''
                  ),
                ])}
              />
            }
          >
            {topRows.length > 0 ? (
              <RankedBars data={topRows} format={fmtInt} emptyNote={notMeasured} />
            ) : (
              <EmptyNote>{notMeasured}</EmptyNote>
            )}
          </ChartCard>
        </Box>
      )}
    </Box>
  );
}

export default ConversationsDashboard;
